import { Box, Typography, Stack, TextField, Autocomplete, Select, MenuItem } from '@mui/material';
import { FC, useState } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { createStorageActivitySubmitValidation } from './activityValidation';
import { Footer } from '@/components/details/Footer';
import { PrivilegeEnum, SampleActivityDto, StatusDto, UpdateSampleActivityRequest } from '@/api/ApiClient';
import { useBackendStore } from '@/stores/backendStore';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useIsDataModified, useSampleActivityData } from '@/hooks/useTreeDetailSubscriptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore } from '@/stores/dialogStore';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { log } from '@/lib/utils/logger';
import { updateSampleActivityWithPreconditionRetry } from '@/lib/tree/submitEntityUtils';
import DetailField from '@/components/common/DetailField';
import { useSubmitWithValidation } from '@/hooks/useSubmitWithValidation';
import { OutlinedPaper, FlexWrapBox } from '@/components/common/StyledComponents';
import { formatDate } from '@/lib/utils/dateUtils';
import { getVisibilityTypeDisplay, VISIBILITY_OPTIONS } from '@/lib/types/visibilityTypes';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createStorageActivitySaveValidation } from '@/lib/validation/saveValidationBuilders';

export const selectOptions: OptionKey[] = ['researcherOptions', 'facultyOptions', 'status'];

const StorageActivity: FC = () => {
  const backend = useBackendStore.getState().getBackend();
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const { resolveUser } = useUserResolution();
  const { setDataModified, updateData, reloadNode } = useTree();
  const { selectedNode } = useTreeSelection();
  const { showSnackbar } = useSnackbar();
  const { showConfirmDialog } = useDialogStore();
  const { submitWithValidation } = useSubmitWithValidation();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Track manual edit mode state (when user clicks "Edit Details" button)
  const [isManuallyEditing, setIsManuallyEditing] = useState(false);

  const activity = useSampleActivityData(selectedNode);

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = !isModified && !isManuallyEditing;

  // Centralized date handling utility
  const convertDateForBackend = (date: Date | null): Date | undefined => {
    return date ? new Date(date.toDateString()) : undefined;
  };

  if (!activity || !selectedNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Select a node to view details
        </Typography>
      </Box>
    );
  }

  if (!options || optionsLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading options...
        </Typography>
      </Box>
    );
  }

  const handleModification = (modified: Partial<SampleActivityDto>) => {
    const updated = { ...activity, ...modified };
    updateData(selectedNode.dataId, updated);
    setDataModified(selectedNode.dataId, true);

    // Clear field errors when user starts editing
    if (modified.sampleLocation !== undefined) {
      clearFieldError('sampleLocation');
    }
  };

  // Clear field errors when user starts editing
  const clearFieldError = (fieldKey: string) => {
    if (fieldErrors[fieldKey]) {
      setFieldErrors((prev: Record<string, string>) => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!activity.id || !selectedNode) return;
    const saveValidation = validateRequiredToSave(createStorageActivitySaveValidation(activity));
    if (!saveValidation.isValid) {
      setFieldErrors(saveValidation.fieldErrors);
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    try {
      const request: UpdateSampleActivityRequest = { ...activity };
      const preconditionDeps = { showSnackbar, showConfirmDialog };
      const updated = await updateSampleActivityWithPreconditionRetry(backend, activity.id, request, preconditionDeps);
      if (updated === null) return;
      updateData(selectedNode.dataId, updated);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false); // Exit edit mode after successful save
      setFieldErrors({});

      // Reload the parent sample to get the latest sample location
      if (selectedNode.parentId) {
        try {
          await reloadNode(selectedNode.parentId, backend);
          log.debug('[StorageActivity] Reloaded parent sample after storage activity update');
        } catch (reloadError) {
          log.error('[StorageActivity] Failed to reload parent sample:', reloadError);
          // Don't show error to user as the main save was successful
        }
      }

      showSnackbar('Storage activity saved successfully', 'success');
    } catch (error) {
      log.error('[StorageActivity] Failed to save activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save storage activity.', 'error');
    }
  };

  // Custom submit handler with validation
  const handleSubmit = async (): Promise<boolean> => {
    if (!activity || !selectedNode) return false;
    const validationFields = createStorageActivitySubmitValidation(activity);
    const result = await submitWithValidation(selectedNode, validationFields, 'Storage Activity');
    if (!result.success && result.fieldErrors) {
      setFieldErrors(result.fieldErrors);
      setIsManuallyEditing(true); // Switch to edit mode to show validation errors
    } else {
      setFieldErrors({});
    }
    return result.success;
  };

  // Handler for Footer's setIsReadOnly callback
  // When value === false: user clicked "Edit Details" → enter edit mode
  // When value === true: user clicked "Cancel" → exit edit mode and clear modifications
  const setIsReadOnly = (value: boolean) => {
    setIsManuallyEditing(!value); // false = edit mode, true = read-only mode
    if (value && selectedNode) {
      // Exiting edit mode: clear any unsaved modifications
      setDataModified(selectedNode.dataId, false);
    }
  };

  const isSubmitted = activity?.status === StatusDto.Submitted;

  // Find the selected researcher for the autocomplete
  const selectedResearcher = options.researcherOptions?.find((r) => r.key === activity.userId) || null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={2}>
          <OutlinedPaper elevation={0} variant="outlined">
            <FlexWrapBox>
              {/* Visibility */}
              <DetailField
                label="Visibility"
                value={activity.visibility ? getVisibilityTypeDisplay(activity.visibility) : 'N/A'}
                isReadOnly={isReadOnly}
              >
                <Select
                  value={activity.visibility ?? ''}
                  onChange={(e) => handleModification({ visibility: e.target.value })}
                  fullWidth
                  size="small"
                >
                  {VISIBILITY_OPTIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {getVisibilityTypeDisplay(value)}
                    </MenuItem>
                  ))}
                </Select>
              </DetailField>

              {/* Date */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DetailField
                  label="Date"
                  value={activity.date ? formatDate(activity.date) : 'N/A'}
                  isReadOnly={isReadOnly || isSubmitted}
                >
                  <DatePicker
                    value={activity.date ? new Date(activity.date) : null}
                    onChange={(newValue) => {
                      handleModification({
                        date: convertDateForBackend(newValue),
                      });
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                </DetailField>
              </LocalizationProvider>

              {/* Sample Location */}
              <DetailField
                label="Sample Location"
                value={activity.sampleLocation || 'Not specified'}
                isReadOnly={isReadOnly}
                requiredToSubmit
                error={fieldErrors.sampleLocation}
              >
                <TextField
                  fullWidth
                  size="small"
                  value={activity.sampleLocation || ''}
                  onChange={(e) => handleModification({ sampleLocation: e.target.value })}
                  placeholder="e.g., Freezer A, Shelf 3, Box 2"
                  error={!!fieldErrors.sampleLocation}
                  helperText={fieldErrors.sampleLocation}
                />
              </DetailField>

              {/* Responsible Person */}
              <DetailField
                label="Responsible Person"
                value={activity.userId ? resolveUser(activity.userId) : 'Not specified'}
                isReadOnly={isReadOnly}
              >
                <Autocomplete
                  options={options.researcherOptions ?? []}
                  getOptionLabel={(option) => option.value}
                  renderOption={(props, option) => (
                    <li {...props} key={option.key}>
                      {option.value}
                    </li>
                  )}
                  value={selectedResearcher}
                  onChange={(_, newValue) => handleModification({ userId: newValue?.key ?? undefined })}
                  autoSelect
                  autoHighlight
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </DetailField>
            </FlexWrapBox>
          </OutlinedPaper>

          {/* Description */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Description:
            </Typography>
            {isReadOnly || isSubmitted ? (
              activity.desc ? (
                <Typography variant="body1">
                  <span dangerouslySetInnerHTML={{ __html: activity.desc }} />
                </Typography>
              ) : (
                <Typography variant="body1">N/A</Typography>
              )
            ) : (
              <RichTextEditor
                value={activity.desc ?? ''}
                onChange={(content) => handleModification({ desc: content })}
              />
            )}
          </Box>
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        <Footer
          lastChangedDate={activity.lastChangedDate}
          lastChangedBy={resolveUser(activity.lastChangedByUser || '')}
          status={activity.status}
          isReadOnly={isReadOnly}
          setIsReadOnly={setIsReadOnly}
          onSave={handleSave}
          privilege={PrivilegeEnum.StorageActivity}
          onSubmit={handleSubmit}
        />
      </Box>
    </Box>
  );
};

export default StorageActivity;
